import { Controller } from 'stimulus'
import { debounce } from 'throttle-debounce'

// Hydrates the member directory cards' geo strips: a live local clock per card, and lazily-fetched
// current weather (batched into one request, cached server-side). Mounted once on a stable wrapper
// so cards added by pagination hydrate via the *TargetConnected callbacks.
export default class extends Controller {
  static targets = ['weatherSlot', 'clock', 'weatherIcon']
  static values = { weatherUrl: String, imperial: Boolean }

  connect() {
    this._pendingCoords = new Set()
    this._flushWeather = debounce(50, this._fetchWeather.bind(this))
    this._tick()
    this._clockTimer = setInterval(() => this._tick(), 60_000)
  }

  disconnect() {
    clearInterval(this._clockTimer)
  }

  // --- Weather -------------------------------------------------------------

  weatherSlotTargetConnected(el) {
    if (!el.dataset.coords || el.dataset.hydrated) return
    this._pendingCoords.add(el.dataset.coords)
    this._flushWeather()
  }

  async _fetchWeather() {
    const coords = [...this._pendingCoords]
    this._pendingCoords.clear()
    if (!coords.length || !this.weatherUrlValue) return

    let readings
    try {
      const url = `${this.weatherUrlValue}?coords=${encodeURIComponent(
        coords.join(';')
      )}`
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return
      readings = await response.json()
    } catch {
      return
    }

    this.weatherSlotTargets.forEach((el) => {
      const reading = readings[el.dataset.coords]
      if (reading && !el.dataset.hydrated) this._renderWeather(el, reading)
    })
  }

  _renderWeather(el, reading) {
    const template =
      this._iconTemplates[this._iconGroup(reading.code, reading.is_day)]
    if (!template) return

    el.dataset.hydrated = 'true'
    el.appendChild(template.content.cloneNode(true))

    if (reading.temp_c != null) {
      const temp = document.createElement('span')
      temp.textContent = this.imperialValue
        ? `${Math.round((reading.temp_c * 9) / 5 + 32)}°F`
        : `${Math.round(reading.temp_c)}°C`
      el.appendChild(temp)
    }
  }

  get _iconTemplates() {
    if (!this._templateMap) {
      this._templateMap = {}
      this.weatherIconTargets.forEach((t) => {
        this._templateMap[t.dataset.group] = t
      })
    }
    return this._templateMap
  }

  // Maps a WMO weather code (https://open-meteo.com/en/docs) to one of our icon templates.
  _iconGroup(code, isDay) {
    if (code === 0) return isDay ? 'clear-day' : 'clear-night'
    if (code <= 2) return isDay ? 'partly-day' : 'partly-night'
    if (code === 3) return 'cloud'
    if (code <= 48) return 'fog'
    if (code <= 67) return 'rain'
    if (code <= 77) return 'snow'
    if (code <= 82) return 'rain'
    if (code <= 86) return 'snow'
    return 'thunder'
  }

  // --- Live clock ----------------------------------------------------------

  clockTargetConnected() {
    this._tick()
  }

  _tick() {
    const now = new Date()
    this.clockTargets.forEach((el) => {
      const iana = el.dataset.iana
      if (!iana) return
      try {
        el.textContent = new Intl.DateTimeFormat([], {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: iana,
        }).format(now)
      } catch {
        // Invalid time zone identifier — keep the server-rendered value.
      }
    })
  }
}
